import React, { useEffect, useState } from "react";
import { Routes, Route, Link, Navigate, useLocation, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Tabs, Tab, Chip } from "@mui/material";
import { Feature, getListById, getListByIdVariables } from "types/gql-types";
import { useFeature } from "components/feature";
import { ServerError, LoadingSpinner } from "components/graphql-views";
import { Viewer } from "components/auth/types";
import { SettingsHeader } from "../pages/settings-header";
import { PROPERTY_CONTRACTS_QUERY, PropertyContractsData } from "../pages/contracts-shared";
import {
  contractForList,
  defaultPropertySettingsPath,
  propertySettingsHeaderChip,
  shouldShowImplementationFormNote,
} from "../sidebar-entries";
import { ListSettingsSubPage } from "./lists/list-settings";
import StatusesSettings from "./stages/settings";
import MembersSettings from "./stages/members";
import BuildingSettings from "./buildings/building-list";
import FundingSources from "./funding";
import { LIST } from "./queries";
import RentLimits from "./rent-limits";
import PropertyInfoPage from "./info";
import PropertyContractSettings from "./contract";
import { ImplementationFormNote } from "./contract/implementation-form-note";

type Props = {
  viewer: Viewer;
}

// eslint-disable-next-line max-lines-per-function, max-statements
const PropertySettingsPage: React.FC<Props> = (props) => {
  const { listId } = useParams();
  const { viewer } = props;
  const location = useLocation();
  const buildingsEnabled = useFeature(Feature.BUILDING_SETTINGS);
  const fundingEnabled = useFeature(Feature.FUNDING_SOURCES);
  const planSelectionEnabled = useFeature(Feature.PLAN_SELECTION);

  const [tabIndex, setTabIndex] = useState(0);

  const { data, loading, error } = useQuery<getListById, getListByIdVariables>(LIST, {
    fetchPolicy: "cache-first",
    variables: { id: listId || "" },
  });
  const { data: contractsData } = useQuery<PropertyContractsData>(PROPERTY_CONTRACTS_QUERY, {
    fetchPolicy: "cache-first",
    skip: !planSelectionEnabled || !listId,
  });

  const contracts = contractsData?.propertyContracts || [];
  const listContract = listId ? contractForList(contracts, listId) : undefined;

  const hasContractForProperty = !!(
    listId &&
    contracts.some(
      (contract) =>
        contract.interestListId === listId ||
        (data?.interestList?.property?.name &&
          contract.propertyName === data.interestList.property.name),
    )
  );

  const tabs = [
    { label: "Lists", path: "lists" },
    { label: "Property Info", path: "info" },
    { label: "Statuses", path: "statuses" },
    { label: "Team Members", path: "members" },
  ];

  if (hasContractForProperty) {
    tabs.splice(2, 0, { label: "Contract", path: "contract" });
  }

  if (buildingsEnabled) {
    tabs.push({ label: "Building Settings", path: "buildings" });
    tabs.push({ label: "Rent Limits", path: "rent-limits" });
  }
  if (fundingEnabled) {
    tabs.push({ label: "Funding Sources", path: "funding" });
  }

  useEffect(() => {
    const currentPath = location.pathname.split("/").pop();
    const activeTabIndex = tabs.findIndex(tab => tab.path === currentPath);
    if (activeTabIndex !== -1) {
      setTabIndex(activeTabIndex);
    }
  }, [location.pathname, tabs]);

  if (!listId) return <ServerError desc="Missing Property ID" />;
  if (error) return <ServerError desc={error.message} />;
  if (loading) return <LoadingSpinner />;
  if (!data) return <ServerError desc="Missing Data" />;

  const { interestList: list } = data;

  const headerChip = propertySettingsHeaderChip(
    { id: list.id, name: list.name, property: { isDraft: list.property?.isDraft } },
    listContract?.status ?? null,
  );

  const defaultTabPath = defaultPropertySettingsPath(list.id, {
    contractStatus: listContract?.status ?? null,
    hasContract: hasContractForProperty,
  }).split("/").pop() || "lists";

  const showImplementationFormNote = shouldShowImplementationFormNote(
    Boolean(list.property?.isDraft),
    listContract?.status ?? null,
  );

  return (
    <div>
      <div style={{ height: "100%", width: "100%" }}>
        <SettingsHeader title={`${list?.name} Property Settings`}>
          {headerChip && (
            <Chip size="small" variant="outlined" color={headerChip.color} label={headerChip.label} />
          )}
        </SettingsHeader>
        <Tabs value={tabIndex} onChange={(_, index) => setTabIndex(index)}>
          {tabs.map(tab => (
            <Tab
              key={tab.path}
              label={tab.label}
              component={Link}
              to={`/dash/manager/settings/property/${listId}/${tab.path}`}
            />
          ))}
        </Tabs>
        <div style={{ paddingTop: 16 }}>
          <ImplementationFormNote show={showImplementationFormNote} />
          <Routes>
            <Route index element={<Navigate to={defaultTabPath} replace />} />
            <Route path="lists" element={<ListSettingsSubPage path="" listId={listId} propertyId={list.property.id} viewer={viewer} />} />
            <Route path="info" element={<PropertyInfoPage path="" propertyId={list.property.id} />} />
            {hasContractForProperty && (
              <Route
                path="contract"
                element={(
                  <PropertyContractSettings
                    listId={listId}
                    propertyName={list.property.name}
                  />
                )}
              />
            )}
            <Route path="statuses" element={<StatusesSettings path="" listId={listId} propertyId={list.property.id} viewer={viewer} />} />
            <Route path="members" element={<MembersSettings path="" listId={listId} isAdmin={viewer.isAdmin} isSupervisor={viewer.isSupervisor} />} />
            {buildingsEnabled && <Route path="buildings" element={<BuildingSettings propertyId={list.property.id} isAdmin={viewer.isAdmin} isSupervisor={viewer.isSupervisor} />} />}
            {fundingEnabled && <Route path="funding" element={<FundingSources propertyId={list.property.id} isAdmin={viewer.isAdmin} isSupervisor={viewer.isSupervisor} />} />}
            {buildingsEnabled && <Route path="rent-limits" element={<RentLimits propertyId={list.property.id} isAdmin={viewer.isAdmin} isSupervisor={viewer.isSupervisor} />} />}
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default PropertySettingsPage;
